import { useMemo } from 'react';

import { EventTrackingAction, UserEvent } from '../declarations';
import { getDeployEnv } from '../internal/deployEnv';
import { useContextualData } from './ContextualDataContext';
import { mergeContextualData } from '../contextualData';
import { globalObservability, TrackingOptions } from '../observability';

/** Gives you access to Observability methods, with ContextualData from the context automatically added. */
export function useObservability(): {
  trackEvent: (
    action: EventTrackingAction,
    label?: string,
    data?: { [k: string]: unknown },
    options?: TrackingOptions,
  ) => void;
  trackUserEvent: (event: UserEvent) => void;
  trackError: (
    label: string,
    error?: Error,
    data?: { [k: string]: unknown },
    options?: TrackingOptions,
  ) => void;
} {
  const env = getDeployEnv();
  const contextualData = useContextualData();
  return useMemo(
    () => ({
      trackEvent: (action, label, data?: { [k: string]: unknown }, options?: TrackingOptions) => {
        if (!globalObservability) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackEvent');
        }
        globalObservability.trackEvent(
          action,
          label,
          mergeContextualData(contextualData, { data }),
          options,
        );
      },
      trackUserEvent: (event: UserEvent) => {
        if (!globalObservability) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackUserEvent');
        }
        globalObservability.trackUserEvent(event, contextualData);
      },
      trackError: (label, error, data?: { [k: string]: unknown }, options?: TrackingOptions) => {
        if (!globalObservability) {
          if (env === 'production') {
            return;
          }
          throw new Error('Call initObservability before trackError');
        }
        globalObservability.trackError(
          label,
          error,
          mergeContextualData(contextualData, { data }),
          options,
        );
      },
    }),
    [contextualData, env],
  );
}
